import { FadeIn } from "../FadeIn";
import { useT } from "../../lib/i18n";
import { TALENTS, TALENT_NAMES } from "../../lib/images";

export const TalentsSection = () => {
  const t = useT();

  return (
    <section
      data-testid="talents-section"
      className="bg-ink section-pad overflow-hidden"
    >
      <div className="container-x">
        <FadeIn>
          <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
            <div className="eyebrow mb-4">{t("talents.eyebrow")}</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-7xl text-bone leading-[0.95] mb-6">
              {t("talents.headlineA")}{" "}
              <span className="italic text-stone">{t("talents.headlineB")}</span>.
            </h2>
            <p className="text-bone/55 text-sm leading-relaxed">
              {t("talents.intro")}
            </p>
          </div>
        </FadeIn>

        {/* Big portraits — horizontal snap-scroll on mobile, grid from sm+ */}
        <div
          className="flex sm:grid sm:grid-cols-2 gap-6 sm:gap-10 lg:gap-16 max-w-5xl mx-auto
                     overflow-x-auto sm:overflow-visible snap-x snap-mandatory scrollbar-hide
                     -mx-6 px-6 sm:mx-0 sm:px-0 pb-6 sm:pb-0"
        >
          {TALENTS.map((talent, idx) => (
            <FadeIn key={talent.id} delay={idx * 0.15}>
              <article
                data-testid={`talent-${talent.id}`}
                className="relative flex flex-col items-center text-center group
                           snap-center shrink-0 w-[82vw] sm:w-auto"
              >
                {/* Radial warm glow */}
                <div
                  className="absolute -inset-8 pointer-events-none transition-opacity duration-700 group-hover:opacity-100 opacity-70"
                  aria-hidden="true"
                  style={{
                    background:
                      "radial-gradient(ellipse at 50% 35%, rgba(184,169,154,0.22) 0%, rgba(184,169,154,0.06) 35%, rgba(0,0,0,0) 70%)",
                  }}
                />

                {/* Portrait */}
                <div className="relative w-56 h-56 sm:w-64 sm:h-64 md:w-72 md:h-72 rounded-3xl overflow-hidden shadow-2xl shadow-black/70 ring-1 ring-stone/20 group-hover:ring-stone/50 transition-all duration-500">
                  <img
                    src={talent.portrait}
                    alt={talent.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top group-hover:scale-[1.06] transition-transform duration-[1400ms] ease-out"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse at 50% 0%, rgba(255,235,200,0.18) 0%, rgba(0,0,0,0) 55%), linear-gradient(to bottom, rgba(0,0,0,0) 55%, rgba(13,13,13,0.55) 100%)",
                    }}
                  />
                </div>

                {/* Name + role */}
                <div className="mt-6 sm:mt-8">
                  <div className="eyebrow text-stone mb-2">{talent.title}</div>
                  <h3 className="font-display text-3xl sm:text-4xl md:text-5xl text-bone leading-[1]">
                    {talent.name}
                  </h3>
                  <div className="text-xs tracking-[0.22em] uppercase text-bone/55 mt-2">
                    {talent.discipline}
                  </div>
                </div>

                {/* Endorsement */}
                <blockquote className="font-display italic text-base sm:text-lg md:text-xl text-bone/80 leading-snug mt-6 sm:mt-8 max-w-sm">
                  « {talent.quote} »
                </blockquote>
              </article>
            </FadeIn>
          ))}
        </div>

        <div className="text-center mt-16 text-[10px] tracking-[0.2em] uppercase text-bone/30">
          Photos · Romain Bruneau
        </div>

        {/* Additional names: typographic strip with tight face-crop avatars */}
        <div className="mt-20">
          <div className="eyebrow text-stone mb-8 text-center">
            Également photographiés
          </div>
          <div className="flex md:grid md:grid-cols-5 gap-px bg-bone/5
                          overflow-x-auto md:overflow-visible snap-x snap-mandatory scrollbar-hide
                          -mx-6 px-6 md:mx-0 md:px-0 pb-2 md:pb-0">
            {TALENT_NAMES.map((person, i) => (
              <FadeIn key={person.name} delay={i * 0.05}>
                <div
                  data-testid={`talent-name-${person.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="bg-ink hover:bg-bone/[0.04] transition-colors py-7 px-3 flex flex-col items-center justify-center text-center group
                             snap-center shrink-0 w-[42vw] md:w-auto"
                >
                  {person.portrait && (
                    <div className="relative mb-4">
                      {/* faint warm halo */}
                      <div
                        className="absolute -inset-2 rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                        aria-hidden="true"
                        style={{
                          background:
                            "radial-gradient(circle, rgba(184,169,154,0.18) 0%, rgba(0,0,0,0) 70%)",
                        }}
                      />
                      <img
                        src={person.portrait}
                        alt={person.name}
                        loading="lazy"
                        width="64"
                        height="64"
                        className="relative w-14 h-14 md:w-16 md:h-16 rounded-full object-cover ring-1 ring-stone/30 group-hover:ring-stone/70 transition-all duration-500"
                        style={{
                          filter: "grayscale(35%) contrast(1.05) brightness(0.96)",
                        }}
                      />
                    </div>
                  )}
                  <div className="font-display text-xl md:text-2xl text-bone leading-tight">
                    {person.name}
                  </div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-stone/80 mt-2">
                    {person.role}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
